import React, { useEffect, useState, useRef } from "react";
import { Button, Card, CardBody, Col, Row, Spinner } from "reactstrap";
import useApiCallService from "../../Common/Helpers/ApiCall";
import { useNavigate } from "react-router-dom";
import { CustomButton } from "../../Common/Components/Button/CustomButton";
import { CustomInput } from "../../Common/Components/Input/CustomInput";
import { ADMIN_PATHS } from "../../config";
import AccountingModel from "../Models/Response/AccountingModel";
import { FlashMessage } from "../../Common/Helpers/FlashMessage";
import { useTranslation } from "react-i18next";
import { CheckUserPermission } from "../../AppSystem/Helpers/CheckUserPermission";
import { CustomModal } from "../../Common/Components/Modal/CustomModal";
import { CustomReload } from "../../AppSystem/Helpers/CustomReload";
import { CustomSubmitButton } from "../../Common/Components/Input/CustomSubmitButton";
import AdminRequestAccountingUpdate from "../Models/Request/AdminRequestAccountingUpdate";
import { useHeader } from "../../Auth/Components/Nav/HeaderContext";
import { CheckMobile } from "../../AppSystem/Helpers/CheckMobile";

const AdminAccountingUpdate = () => {
    const { t } = useTranslation();
    const { ApiCall } = useApiCallService();
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get('id') ?? "";
    const navigate = useNavigate();
    const isMountedRef = useRef(true);
    const isMobile = CheckMobile(998);

    const [accounting, setAccounting] = useState(new AccountingModel());
    const [editAccounting, setEditAccounting] = useState(new AdminRequestAccountingUpdate());
    const [displaySpinner, setDisplaySpinner] = useState(false);
    const [responseDelete, setResponseDelete] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [touched, setTouched] = useState({});
    const { setPageTitle, setPageTitleKey, setOnBack } = useHeader();

    useEffect(() => {
        setPageTitle(t('accounting.update'));
        setPageTitleKey('accounting.update');
        setOnBack(() => { navigate(ADMIN_PATHS.ADMIN_ACCOUNTING_LIST); });
        return () => { setPageTitle(''); setPageTitleKey(''); setOnBack(null); };
    }, [t, navigate]);

    // MODALS
    const [displayDeleteModal, setDisplayDeleteModal] = useState(false);
    const [displayReactivateModal, setDisplayReactivateModal] = useState(false);

    // Documentation options
    const documentationOptions = AdminRequestAccountingUpdate.getDocumentationOptions();

    let permission = false;
    if (CheckUserPermission("system.setting.account-settings") || CheckUserPermission("system.setting") || CheckUserPermission("system.setting.account-settings.update") || CheckUserPermission("system")) {
        permission = true;
    }

    let permissionDelete = false;
    if (CheckUserPermission("system.setting.account-settings") || CheckUserPermission("system.setting") || CheckUserPermission("system.setting.account-settings.delete") || CheckUserPermission("system")) {
        permissionDelete = true;
    }

    // track mount/unmount
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // GET ACCOUNTING (only when id changes)
    useEffect(() => {
        if (!id) return;

        const handleApiResponse = (apiResponse) => {
            if (!isMountedRef.current) return;
            setAccounting(new AccountingModel(apiResponse));
            setEditAccounting(new AdminRequestAccountingUpdate(apiResponse));
        };

        const fetchData = async () => {
            try {
                const apiCallOptions = {
                    method: "get",
                    url: `/accounting?id=${id}`
                };
                await ApiCall(apiCallOptions, handleApiResponse);
            } catch (error) {
                console.log('error fetching accounting', error);
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const valueInput = document.getElementById('value');
    if (valueInput) {
        valueInput.addEventListener('keypress', function (e) {
            const key = e.key;

            if (!/^[0-9]$/.test(key)) {
                e.preventDefault();
            return;
            }

            const start = this.selectionStart ?? 0;
            const end = this.selectionEnd ?? 0;
            const newLength = this.value.length - (end - start) + 1;
            if (newLength > 4) {
                e.preventDefault();
            }
        });
    }

    const handleChangeAccounting = (e, name) => {
        let value;
        if (e && e.target) {
            value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        }

        setEditAccounting((prevFormData) => ({
            ...prevFormData,
            [name]: value,
        }));

        // clear field error while editing
        setFieldErrors(prev => {
            const copy = { ...prev };
            delete copy[name];
            return copy;
        });
    };

    // Handle checkbox changes for required documentation
    const handleDocumentationChange = (docValue, isChecked) => {
        setEditAccounting((prev) => {
            const currentDocs = prev.requiredDocumentation || [];
            let newDocs;

            if (isChecked) {
                if (!currentDocs.includes(docValue)) {
                    newDocs = [...currentDocs, docValue];
                } else {
                    newDocs = currentDocs;
                }
            } else {
                newDocs = currentDocs.filter(doc => doc !== docValue);
            }

            return {
                ...prev,
                requiredDocumentation: newDocs
            };
        });
    };

    useEffect(() => {
        if (errorMessage !== '') {
            FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
            setDisplaySpinner(false);
            setErrorMessage('');
            setTimeout(() => { CustomReload(); }, 4800);
        }
    }, [errorMessage, t]);

    // --- helper: check existence using 'list' fallback; excludes current id
    const checkExists = async (field, value) => {
        if (!value) return false;
        const trimmed = (value || '').trim().toLowerCase();
        try {
            const response = await new Promise((resolve, reject) => {
                ApiCall({
                    method: "get",
                    url: `/accounting/list?query=${encodeURIComponent(value)}`
                }, resolve, reject);
            });

            const items = (response && (response.items || response)) || [];
            if (!Array.isArray(items)) return false;

            // exclude the current record by id
            const currentId = editAccounting?.id || accounting?.id || id || null;

            return items.some(it => {
                if (!it) return false;
                const compare = field === 'name' ? (it.name || '') : (it.value || '');
                if (!compare) return false;
                if (currentId && it.id === currentId) return false; // same record -> ignore
                return compare.trim().toLowerCase() === trimmed;
            });
        } catch (e) {
            console.warn("checkExists fallback failed", e);
            return false;
        }
    };

    // onBlur handlers to show inline errors
    const handleBlurName = async () => {
        setTouched(prev => ({ ...prev, name: true }));
        if (!editAccounting.name || editAccounting.name.trim() === "") {
            setFieldErrors(prev => ({ ...prev, name: 'validationMessage.fieldRequired' }));
            return;
        }
        try {
            const exists = await checkExists('name', editAccounting.name);
            if (exists) {
                setFieldErrors(prev => ({ ...prev, name: 'validationMessage.nameExists' }));
            } else {
                setFieldErrors(prev => { const c = { ...prev }; delete c.name; return c; });
            }
        } catch (e) {
            console.error(e);
        }
    };

    const handleBlurValue = async () => {
        setTouched(prev => ({ ...prev, value: true }));
        if (!editAccounting.value || editAccounting.value.trim() === "") {
            setFieldErrors(prev => ({ ...prev, value: 'validationMessage.fieldRequired' }));
            return;
        }
        try {
            const exists = await checkExists('value', editAccounting.value);
            if (exists) {
                setFieldErrors(prev => ({ ...prev, value: 'validationMessage.accountExists' }));
            } else {
                setFieldErrors(prev => { const c = { ...prev }; delete c.value; return c; });
            }
        } catch (e) {
            console.error(e);
        }
    };

    const handleValidation = async () => {
        setDisplaySpinner(true);

        const schema = AdminRequestAccountingUpdate.getAttributes(null, true);

        try {
            // yup full validation
            await schema.validate(editAccounting, { abortEarly: false });

            // check duplicates server-side / via search (exclude current id)
            const [nameExists, valueExists] = await Promise.all([
                checkExists('name', editAccounting.name),
                checkExists('value', editAccounting.value)
            ]);

            if (nameExists) {
                setFieldErrors(prev => ({ ...prev, name: 'validationMessage.nameExists' }));
                setTouched(prev => ({ ...prev, name: true }));
                setDisplaySpinner(false);
                return;
            }
            if (valueExists) {
                setFieldErrors(prev => ({ ...prev, value: 'validationMessage.accountExists' }));
                setTouched(prev => ({ ...prev, value: true }));
                setDisplaySpinner(false);
                return;
            }

            const AccountingSendHandler = (apiResponse) => {
                if (!isMountedRef.current) return;
                
                // Verificar si es un error disfrazado de éxito
                if (apiResponse && typeof apiResponse === 'string' && apiResponse.includes('validation.duplicate')) {
                    const errorMsg = apiResponse;

                    if (errorMsg.includes('validation.duplicateValue')) {
                        FlashMessage(t('validationMessage.accountExists'), 'flash-messageError');
                        setFieldErrors(prev => ({ ...prev, value: 'validationMessage.accountExists' }));
                        setTouched(prev => ({ ...prev, value: true }));
                    } else if (errorMsg.includes('validation.duplicateName')) {
                        FlashMessage(t('validationMessage.nameExists'), 'flash-messageError');
                        setFieldErrors(prev => ({ ...prev, name: 'validationMessage.nameExists' }));
                        setTouched(prev => ({ ...prev, name: true }));
                    } else if (errorMsg.includes('validation.duplicateEntry')) {
                        FlashMessage(t('validationMessage.duplicateEntry'), 'flash-messageError');
                        setFieldErrors(prev => ({ 
                            ...prev, 
                            name: 'validationMessage.duplicateEntry',
                            value: 'validationMessage.duplicateEntry'
                        }));
                        setTouched(prev => ({ ...prev, name: true, value: true }));
                    } else {
                        FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
                    }
                    
                    setDisplaySpinner(false);
                    return;
                }
                
                // Es un éxito real
                if (apiResponse) {
                    setAccounting(apiResponse);
                    setDisplaySpinner(false);
                    FlashMessage(t('validationMessage.accountingUpdate'), 'flash-messageSuccess');
                    setTimeout(() => {
                        navigate(`${ADMIN_PATHS.ADMIN_ACCOUNTING_LIST}`);
                    }, 2000);
                }
            };

            // Build URL with indexed parameters for array
            let url = `/accounting/update?id=${id}&name=${encodeURIComponent(editAccounting.name ?? "")}&value=${encodeURIComponent(editAccounting.value ?? "")}&message=${encodeURIComponent(editAccounting.message ?? "")}`;
            
            const reqDocs = editAccounting.requiredDocumentation || [];
            reqDocs.forEach((doc, index) => {
                url += `&requiredDocumentation[${index}]=${encodeURIComponent(doc)}`;
            });

            const apiCallOptions = {
                method: "put",
                url: url,
            };
            try {
                await ApiCall(apiCallOptions, AccountingSendHandler, setErrorMessage);
            } catch (error) {
                console.log(error);
                FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
                setDisplaySpinner(false);
            }
        } catch (err) {
            // yup validation errors -> show inline
            const newErrors = {};
            if (err && err.inner && err.inner.length > 0) {
                err.inner.forEach(e => {
                    if (e.path) newErrors[e.path] = e.message;
                });
            } else if (err && err.path) {
                newErrors[err.path] = err.message;
            }
            setFieldErrors(newErrors);
            const newTouched = { ...touched };
            Object.keys(newErrors).forEach(k => { newTouched[k] = true; });
            setTouched(newTouched);
            setDisplaySpinner(false);
        }
    };

    // HANDLE MODAL DELETE
    const HandleDeleteAccounting = async () => {
        if (id !== "") {
            const apiCallOptions = {
                method: "delete",
                url: `/accounting?id=${id}`,
            };
            try {
                await ApiCall(apiCallOptions, setResponseDelete);
                setDisplayDeleteModal(false);
            } catch (error) {
                console.log({ error });
            }
        }
    };

    useEffect(() => {
        if (responseDelete !== null) {
            CustomReload(ADMIN_PATHS.ADMIN_ACCOUNTING_LIST);
        }
    }, [responseDelete]);

    const modalContentDelete = (
        <div>
            {`${t('validationMessage.modalDelete')}`}
        </div>
    );

    const modalFooterDelete = (
        <>
            <Button className="box-green border-0" onClick={HandleDeleteAccounting}>
                {t('button.delete')}
            </Button>
            <Button className="box-red border-0" onClick={() => setDisplayDeleteModal(false)}>
                {t('button.cancel')}
            </Button>
        </>
    );

    const modalContentReactivate = (
        <div>
            {`${t('validationMessage.modalReactivate')}`}
        </div>
    );

    const modalFooterReactivate = (
        <>
            <Button
                className="box-green border-0"
                onClick={async () => {
                    try {
                        setDisplaySpinner(true);
                        const apiCallOptions = {
                            method: "put",
                            url: `/accounting/reactivate?id=${id}`,
                        };
                        await ApiCall(apiCallOptions, (apiResponse) => {
                            if (!isMountedRef.current) return;
                            setEditAccounting(apiResponse);
                            setDisplaySpinner(false);
                            FlashMessage(t('validationMessage.accountingReactivated'), 'flash-messageSuccess');
                        });
                        setDisplayReactivateModal(false);
                    } catch (error) {
                        console.log(error);
                        FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
                        setDisplaySpinner(false);
                    }
                }}
            >
                {t('button.reactivate')}
            </Button>
            <Button
                className="box-red border-0"
                onClick={() => setDisplayReactivateModal(false)}
            >
                {t('button.cancel')}
            </Button>
        </>
    );

    const renderFieldErrorAbove = (fieldName) => {
        const err = fieldErrors[fieldName];
        const translated = err ? (typeof err === 'string' ? t(err) : err) : '';
        return (
            <div className={`error tooltip-custom-normal position-absolute ${err ? 'opacity-1' : 'opacity-0'}`}
                style={{ color: '#f56e6e' }}>
                {translated}
            </div>
        );
    };

    return (
        <>
            {/* Modal de eliminar */}
            <CustomModal
                isOpen={displayDeleteModal}
                toggle={() => setDisplayDeleteModal(!displayDeleteModal)}
                body={modalContentDelete}
                footer={modalFooterDelete}
            />

            {/* Modal de reactivar */}
            <CustomModal
                isOpen={displayReactivateModal}
                toggle={() => setDisplayReactivateModal(!displayReactivateModal)}
                body={modalContentReactivate}
                footer={modalFooterReactivate}
            />

            {!isMobile && (
                <Col lg={12}>
                    <CustomButton
                        text={t('button.back')}
                        icon={'bx bx-chevron-left'}
                        buttonSize={'small'}
                        customClasses={'ms-3 mb-3'}
                        onClick={() => navigate(ADMIN_PATHS.ADMIN_ACCOUNTING_LIST)}
                    />
                </Col>
            )}

            <Row className="pb-4 px-0 mx-0">
                <Col lg={6} className="px-0 mx-0 mb-3 pe-lg-2">
                    <Card className="card-backend p-4">
                        <Row>
                            <Col lg={12}>
                                <h2 className="rokkitt-font">{t('accounting.update')}</h2>
                            </Col>
                            <Col lg={12}>
                                <Row>
                                    <Col lg={12}>
                                        <div className={'mb-3 position-relative'} style={{ paddingTop: '10px' }}>
                                            {renderFieldErrorAbove('value')}
                                            <CustomInput
                                                model={AdminRequestAccountingUpdate}
                                                attribute='value'
                                                data={editAccounting.value}
                                                customChange={(e) => handleChangeAccounting(e, 'value')}
                                                inputProps={{ id: 'value', onBlur: handleBlurValue }}
                                            />
                                        </div>
                                    </Col>
                                    <Col lg={12}>
                                        <div className={'mb-3 position-relative'} style={{ paddingTop: '10px' }}>
                                            {renderFieldErrorAbove('name')}
                                            <CustomInput
                                                model={AdminRequestAccountingUpdate}
                                                attribute='name'
                                                data={editAccounting.name}
                                                customChange={(e) => handleChangeAccounting(e, 'name')}
                                                inputProps={{ id: 'name', onBlur: handleBlurName }}
                                            />
                                        </div>
                                    </Col>
                                    <Col lg={12}>
                                        <div className="mb-3">
                                            <label>{t('accounting.message')}</label>
                                            <textarea
                                                className="form-control"
                                                value={editAccounting.message || ''}
                                                onChange={(e) => handleChangeAccounting(e, "message")}
                                                style={{ resize: "vertical", minHeight: "60px", width: "100%", overflow: "auto" }}
                                                rows={2}
                                            />
                                        </div>
                                    </Col>

                                    <Col lg={12} className="text-end pt-5">
                                        {id !== "" && permissionDelete &&
                                            (editAccounting.deleted
                                                ? (
                                                    <Button
                                                        className="rounded-2 box-green border-0 me-2"
                                                        onClick={() => setDisplayReactivateModal(true)}
                                                    >
                                                        <i className="bx bx-reset fs-5 pe-1" /> {t("button.reactivate")}
                                                    </Button>
                                                )
                                                : (
                                                    <Button
                                                        className="rounded-2 box-red border-0 me-2"
                                                        onClick={() => setDisplayDeleteModal(true)}
                                                    >
                                                        <i className="bx bx-x-circle fs-5 pe-1" /> {t("button.delete")}
                                                    </Button>
                                                )
                                            )
                                        }

                                        {displaySpinner && (
                                            <>
                                                {t('validationMessage.updateAccounting')} <Spinner color='success' className="me-3" />
                                            </>
                                        )}

                                        <CustomSubmitButton
                                            text={t('button.update')}
                                            id='submitAccounting'
                                            icon='bx bx-save pe-2'
                                            customClick={async () => {
                                                await handleValidation();
                                            }}
                                        />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Card>
                </Col>

                {/* Right Column - Required Documentation */}
                <Col lg={6} className="px-0 mx-0 ps-lg-2 mb-4">
                    {permission && (
                        <Card className="card-backend p-4">
                            <CardBody>
                                <h5 className="mb-3 text-green fw-bold">{t('accounting.requiredDocumentation')}</h5>
                                <p className="text-muted mb-3">{t('accounting.selectRequiredDocs')}</p>
                                {documentationOptions.map((option) => (
                                    <div key={option.value} className="form-check mb-3">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`doc-${option.value}`}
                                            checked={(editAccounting.requiredDocumentation || []).includes(option.value)}
                                            onChange={(e) => handleDocumentationChange(option.value, e.target.checked)}
                                        />
                                        <label className="form-check-label" htmlFor={`doc-${option.value}`}>
                                            {t(option.label)}
                                        </label>
                                    </div>
                                ))}
                            </CardBody>
                        </Card>
                    )}
                </Col>
            </Row>
        </>
    );
};

export default AdminAccountingUpdate;